import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  Multicolumn,
  ReportReadStatus,
  ReportReadSwitchStatus,
  ReportSourceType,
} from '@/core/interfaces/common';
import { applyAllFilters, applyFilter, resetFilters } from '@/core/store/reducers/filtersSlice';

import {
  Report,
  ReportResponseItemWithReadStatus,
  ReportsState,
} from '@/features/Reports/interfaces';

import {
  getHorizonReportsList,
  getHorizonReportsListForMulticolumn,
  getLookoutReportsList,
  getLookoutReportsListForMulticolumn,
} from './actions';

const initialState: ReportsState = {
  readSwitchStatus: ReportReadSwitchStatus.UNREAD,
  horizonReportsList: [],
  lookoutReportsList: [],
  activeReportData: null,
  multicolumnsList: [],
  horizonReportsListLoading: false,
  lookoutReportsListLoading: false,
  multicolumnHorizonLoading: {},
  multicolumnLookoutLoading: {},
};

export const reportDetailsSlice = createSlice({
  name: 'reportDetails',
  initialState,
  reducers: {
    openDetailsModal: (state, { payload }: PayloadAction<Report>) => {
      state.activeReportData = payload;
    },
    closeDetailsModal: state => {
      state.activeReportData = null;
    },
    appendHorizonReports: (
      state,
      { payload }: PayloadAction<Array<ReportResponseItemWithReadStatus>>
    ) => {
      const existingIds = new Set(state.horizonReportsList.map(report => report.reportId));
      const reportsList = payload
        .filter(item => !existingIds.has(item.reportId))
        .map<Report>(item => ({
          ...item,
          reportSourceType: ReportSourceType.HORIZON,
          wasOpened: false,
        }));

      state.horizonReportsList.push(...reportsList);
    },
    appendLookoutReports: (
      state,
      { payload }: PayloadAction<Array<ReportResponseItemWithReadStatus>>
    ) => {
      const existingIds = new Set(state.lookoutReportsList.map(report => report.reportId));
      const reportsList = payload
        .filter(item => !existingIds.has(item.reportId))
        .map<Report>(item => ({
          ...item,
          reportSourceType: ReportSourceType.LOOKOUT,
          wasOpened: false,
        }));

      state.lookoutReportsList.push(...reportsList);
    },
    markReportAsOpened: (
      state,
      {
        payload: { reportId, reportSourceType, columnId },
      }: PayloadAction<{
        reportId: number;
        reportSourceType: ReportSourceType;
        columnId?: number;
      }>
    ) => {
      let reportsList: Array<Report> = [];

      if (columnId) {
        const column = state.multicolumnsList.find(item => item.id === columnId);

        if (column) {
          if (reportSourceType === ReportSourceType.HORIZON) {
            reportsList = column.horizonReports;
          }

          if (reportSourceType === ReportSourceType.LOOKOUT) {
            reportsList = column.lookoutReports;
          }
        }
      } else {
        if (reportSourceType === ReportSourceType.HORIZON) {
          reportsList = state.horizonReportsList;
        }

        if (reportSourceType === ReportSourceType.LOOKOUT) {
          reportsList = state.lookoutReportsList;
        }
      }

      const report = reportsList.find(item => item.reportId === reportId);

      if (report) {
        report.wasOpened = true;
      }
    },
    changeReportReadStatus: (
      state,
      {
        payload: { reportId, reportSourceType, readStatus },
      }: PayloadAction<{
        reportId: number;
        reportSourceType: ReportSourceType;
        readStatus: ReportReadStatus;
      }>
    ) => {
      const affectedReports: Array<Report> = [];

      const columns = state.multicolumnsList.filter(
        column =>
          column.horizonReports.some(item => item.reportId === reportId) ||
          column.lookoutReports.some(item => item.reportId === reportId)
      );

      columns.forEach(column => {
        if (reportSourceType === ReportSourceType.HORIZON) {
          const report = column.horizonReports.find(item => item.reportId === reportId);

          if (report) {
            affectedReports.push(report);
          }
        }

        if (reportSourceType === ReportSourceType.LOOKOUT) {
          const report = column.lookoutReports.find(item => item.reportId === reportId);

          if (report) {
            affectedReports.push(report);
          }
        }
      });

      if (reportSourceType === ReportSourceType.HORIZON) {
        const report = state.horizonReportsList.find(item => item.reportId === reportId);

        if (report) {
          affectedReports.push(report);
        }
      }

      if (reportSourceType === ReportSourceType.LOOKOUT) {
        const report = state.lookoutReportsList.find(item => item.reportId === reportId);

        if (report) {
          affectedReports.push(report);
        }
      }

      affectedReports.forEach(report => {
        report.readStatus = readStatus;
      });
    },
    addMulticolumn: (state, { payload }: PayloadAction<Multicolumn>) => {
      if (state.multicolumnsList.some(column => column.id === payload.id)) {
        return;
      }

      state.multicolumnsList.push(payload);
    },
    removeMulticolumn: (state, { payload }: PayloadAction<Multicolumn['id']>) => {
      state.multicolumnsList = state.multicolumnsList.filter(column => column.id !== payload);
    },
    setMulticolumnsOrder: (state, { payload }: PayloadAction<Array<Multicolumn>>) => {
      state.multicolumnsList = payload;
    },
    appendMulticolumnHorizonReport: (
      state,
      {
        payload,
      }: PayloadAction<{
        focusId: number;
        reports: Array<Report>;
      }>
    ) => {
      state.multicolumnsList = state.multicolumnsList.map(column => {
        if (column.id === payload.focusId) {
          const existingIds = new Set(column.horizonReports.map(report => report.reportId));
          const newReports = payload.reports.filter(report => !existingIds.has(report.reportId));

          column.horizonReports.push(...newReports);
        }

        return column;
      });
    },
    appendMulticolumnLookoutReport: (
      state,
      {
        payload,
      }: PayloadAction<{
        focusId: number;
        reports: Array<Report>;
      }>
    ) => {
      state.multicolumnsList = state.multicolumnsList.map(column => {
        if (column.id === payload.focusId) {
          const existingIds = new Set(column.lookoutReports.map(report => report.reportId));
          const newReports = payload.reports.filter(report => !existingIds.has(report.reportId));

          column.lookoutReports.push(...newReports);
        }

        return column;
      });
    },
    changeReportsReadSwitchStatus: (state, { payload }: PayloadAction<ReportReadSwitchStatus>) => {
      state.readSwitchStatus = payload;
    },
    changeMulticolumnReportsReadSwitchStatus: (
      state,
      { payload }: PayloadAction<{ focusId: number; readSwitchStatus: ReportReadSwitchStatus }>
    ) => {
      const multicolumn = state.multicolumnsList.find(column => column.id === payload.focusId);

      if (multicolumn) {
        multicolumn.readSwitchStatus = payload.readSwitchStatus;
      }
    },
    clearReportsLists: state => {
      state.horizonReportsList = [];
      state.lookoutReportsList = [];
      state.multicolumnsList.forEach(column => {
        column.horizonReports = [];
        column.lookoutReports = [];
      });
    },
    updateReportSources: (
      state,
      {
        payload: { reportId, reportSourceType, sources },
      }: PayloadAction<{
        reportId: number;
        reportSourceType: ReportSourceType;
        sources: Report['source'];
      }>
    ) => {
      const updateReportInList = (report: Report) => {
        if (report.reportId === reportId) {
          report.source = sources;
        }
      };

      if (state.activeReportData?.reportId === reportId) {
        state.activeReportData.source = sources;
      }

      if (reportSourceType === ReportSourceType.HORIZON) {
        state.horizonReportsList.forEach(updateReportInList);
      }

      if (reportSourceType === ReportSourceType.LOOKOUT) {
        state.lookoutReportsList.forEach(updateReportInList);
      }

      state.multicolumnsList.forEach(column => {
        if (reportSourceType === ReportSourceType.HORIZON) {
          column.horizonReports.forEach(updateReportInList);
        }

        if (reportSourceType === ReportSourceType.LOOKOUT) {
          column.lookoutReports.forEach(updateReportInList);
        }
      });
    },
  },
  extraReducers: builder =>
    builder
      .addCase(applyFilter, state => {
        state.horizonReportsList = [];
        state.lookoutReportsList = [];
      })
      .addCase(applyAllFilters, state => {
        state.horizonReportsList = [];
        state.lookoutReportsList = [];
      })
      .addCase(resetFilters, state => {
        state.horizonReportsList = [];
        state.lookoutReportsList = [];
      })
      .addCase(getHorizonReportsList.pending, state => {
        if (state.horizonReportsList.length === 0) {
          state.horizonReportsListLoading = true;
        }
      })
      .addCase(getHorizonReportsList.fulfilled, state => {
        state.horizonReportsListLoading = false;
      })
      .addCase(getHorizonReportsList.rejected, state => {
        state.horizonReportsListLoading = false;
      })
      .addCase(getLookoutReportsList.pending, state => {
        if (state.lookoutReportsList.length === 0) {
          state.lookoutReportsListLoading = true;
        }
      })
      .addCase(getLookoutReportsList.fulfilled, state => {
        state.lookoutReportsListLoading = false;
      })
      .addCase(getLookoutReportsList.rejected, state => {
        state.lookoutReportsListLoading = false;
      })
      .addCase(getHorizonReportsListForMulticolumn.pending, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          const column = state.multicolumnsList.find(c => c.id === focusId);

          if (!column?.horizonReports?.length) {
            state.multicolumnHorizonLoading[focusId] = true;
          }
        }
      })
      .addCase(getHorizonReportsListForMulticolumn.fulfilled, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          state.multicolumnHorizonLoading[focusId] = false;
        }
      })
      .addCase(getHorizonReportsListForMulticolumn.rejected, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          state.multicolumnHorizonLoading[focusId] = false;
        }
      })
      .addCase(getLookoutReportsListForMulticolumn.pending, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          const column = state.multicolumnsList.find(c => c.id === focusId);

          if (!column?.lookoutReports?.length) {
            state.multicolumnLookoutLoading[focusId] = true;
          }
        }
      })
      .addCase(getLookoutReportsListForMulticolumn.fulfilled, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          state.multicolumnLookoutLoading[focusId] = false;
        }
      })
      .addCase(getLookoutReportsListForMulticolumn.rejected, (state, action) => {
        const focusId = action.meta.arg?.focusId;

        if (typeof focusId === 'number') {
          state.multicolumnLookoutLoading[focusId] = false;
        }
      }),
});

export const {
  appendHorizonReports,
  appendLookoutReports,
  openDetailsModal,
  closeDetailsModal,
  markReportAsOpened,
  addMulticolumn,
  removeMulticolumn,
  setMulticolumnsOrder,
  appendMulticolumnHorizonReport,
  appendMulticolumnLookoutReport,
  changeReportsReadSwitchStatus,
  changeMulticolumnReportsReadSwitchStatus,
  changeReportReadStatus,
  clearReportsLists,
  updateReportSources,
} = reportDetailsSlice.actions;

export default reportDetailsSlice.reducer;
